/**
 * /launches — full-screen Starship launch panel.
 *
 * The same content as the homepage's desktop Starship panel (mission name +
 * location, live LED countdown, and the 12 upcoming launches over the Starbase
 * photo) blown up to fill the whole viewport. The mobile "Next Launch" card on
 * the homepage taps through to here; it also works as a standalone immersive
 * page on desktop.
 *
 * Reuses the shared .hr-ss-* / .hr-cd-* classes from homepage-redesign.css for
 * the inner content; LaunchesPage.css only adds the full-viewport frame.
 */

import { useEffect, useState } from 'react';

import { getLaunches } from '../api/launches.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import CountdownClock, { countdownParts, shortDate } from '../components/CountdownClock.jsx';
import './homepage-redesign.css';   // shared .hr-ss-* / .hr-cd-* / overlay classes
import './LaunchesPage.css';


export default function LaunchesPage() {
  usePageTitle('Launches');

  const [starship, setStarship] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getLaunches()
      .then((data) => {
        if (cancelled) return;
        setStarship(data?.starship ?? null);
        setUpcoming(Array.isArray(data?.upcoming) ? data.upcoming : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Live clock — drives the countdown, ticks once per second.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const cd = starship ? countdownParts(starship.net, nowMs) : null;

  return (
    <div className="launches-page">
      {/* right-side dark gradient, same as the homepage panel */}
      <div className="hr-starship-overlay" />

      <div className="launches-inner">
        {/* Mission hero — hidden entirely when there's no Starship flight */}
        {starship && (
          <div className="hr-ss-hero">
            <div className="hr-ss-name">{starship.name}</div>
            {starship.location && <div className="hr-ss-loc">{starship.location}</div>}
            <div className="hr-ss-countdown">
              <CountdownClock cd={cd} />
            </div>
          </div>
        )}

        {/* Equal spacers center the upcoming list vertically */}
        <div className="hr-ss-spacer" />

        <div className="hr-ss-upcoming">
          <div className="hr-ss-upcoming-label">Upcoming Launches</div>
          <ul className="hr-ss-list">
            {upcoming.map((l, i) => (
              <li className="hr-ss-launch" key={i}>
                <span className="hr-ss-launch-name">{l.name}</span>
                <span className="hr-ss-launch-date">{shortDate(l.net)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="hr-ss-spacer" />
      </div>
    </div>
  );
}
